import { Col, Input, Row, Select, Typography } from "antd";
import { useStoreOutgoingPostbackMutation } from "api/TenantSlug/OutgoingPostback/hooks";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { AdvertiserSelectSearchable } from "components/UI/Selects/AdvertiserSelectSearchable/AdvertiserSelectSearchable.tsx";
import { CascadeSelectSearchable } from "components/UI/Selects/CascadeSelectSearchable/CascadeSelectSearchable.tsx";
import { ConversionTypeSelectSearchable } from "components/UI/Selects/ConversionTypeSelectSearchable/ConversionTypeSelectSearchable.tsx";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { FormStructureSelectSearchable } from "components/UI/Selects/FormStructureSelectSearchable/FormStructureSelectSearchable.tsx";
import { IntegrationSelectSearchable } from "components/UI/Selects/IntegrationSelectSearchable/IntegrationSelectSearchable.tsx";
import { OfferSelectSearchable } from "components/UI/Selects/OfferSelectSearchable/OfferSelectSearchable.tsx";
import { PartnerSelectSearchable } from "components/UI/Selects/PartnerSelectSearchable/PartnerSelectSearchable.tsx";
import { PayoutResultSelectSearchable } from "components/UI/Selects/PayoutResultSelectSearchable/PayoutResultSelectSearchable.tsx";
import { PayoutTypeSelectSearchable } from "components/UI/Selects/PayoutTypeSelectSearchable/PayoutTypeSelectSearchable.tsx";
import { SourceSelectSearchable } from "components/UI/Selects/SourceSelectSearchable/SourceSelectSearchable.tsx";
import { StatusSelectSearchable } from "components/UI/Selects/StatusSelectSearchable/StatusSelectSearchable.tsx";
import { TriggerSelectSearchable } from "components/UI/Selects/TriggerSelectSearchable/TriggerSelectSearchable.tsx";
import { UserSelectSearchable } from "components/UI/Selects/UserSelectSearchable/UserSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { MULTISELECT_ALL_VALUE, TOAST_TYPES } from "localConstants";
import { FC, useEffect, useRef } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { OutgoingPostbackTrigger, PermissionEnum, RoleEnum } from "types/enums";
import { showCustomToast } from "utils/toast.ts";

import { AddTrackingToolToFormData, schema } from "./formSchema";

import StoreOutgoingPostbackRequest = App.Http.Requests.Tracking.StoreOutgoingPostbackRequest;
interface AddTrackingToolToFormProps {
    onSubmit?: () => void;
    formId?: string;
}

const hasSameValues = <T,>(current: T[], next: T[]) =>
    current.length === next.length && current.every((value, index) => value === next[index]);

export const AddTrackingToolToFormContent: FC<AddTrackingToolToFormProps> = () => {
    const { t } = usePaldockTranslation("forms.add-tracking-tool-to");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<AddTrackingToolToFormData>();
    const { control, fieldNames, watch, setValue } = generalFormUtils;
    const permissionsUtils = usePermission();
    const canSetOwner = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetOwner);
    const userUtils = useUser();
    const currentUser = userUtils.getAuthenticatedUser();
    const isAffiliatePartner =
        userUtils.getActiveTenant()?.userRoles?.some((role) => role.name === RoleEnum.AffiliatePartner) ?? false;

    const includedOffers = watch("offer_ids") ?? [];
    const excludedOffers = watch("excluded_offer_ids") ?? [];
    const includedIsAll = includedOffers.includes(MULTISELECT_ALL_VALUE);
    const excludedIsAll = excludedOffers.includes(MULTISELECT_ALL_VALUE);
    const includedHasSpecific = includedOffers.length > 0 && !includedIsAll;
    const excludedHasSpecific = excludedOffers.length > 0 && !excludedIsAll;
    const previousIncludedIsAll = useRef(includedIsAll);
    const previousExcludedIsAll = useRef(excludedIsAll);

    useEffect(() => {
        const wasIncludedAll = previousIncludedIsAll.current;
        const wasExcludedAll = previousExcludedIsAll.current;

        if (!includedOffers.length && !excludedOffers.length) {
            const nextIncludedOffers =
                wasExcludedAll && !wasIncludedAll ? [MULTISELECT_ALL_VALUE] : ([] as typeof includedOffers);
            const nextExcludedOffers =
                wasIncludedAll && !wasExcludedAll ? [MULTISELECT_ALL_VALUE] : ([] as typeof excludedOffers);

            if (!hasSameValues(includedOffers, nextIncludedOffers)) {
                setValue("offer_ids", nextIncludedOffers);
            }

            if (!hasSameValues(excludedOffers, nextExcludedOffers)) {
                setValue("excluded_offer_ids", nextExcludedOffers);
            }
        } else if (includedIsAll && excludedIsAll) {
            if (!wasIncludedAll && wasExcludedAll && excludedOffers.length > 0) {
                setValue("excluded_offer_ids", []);
            } else if (!wasExcludedAll && wasIncludedAll && includedOffers.length > 0) {
                setValue("offer_ids", []);
            }
        }

        previousIncludedIsAll.current = includedIsAll;
        previousExcludedIsAll.current = excludedIsAll;
    }, [excludedIsAll, excludedOffers, includedIsAll, includedOffers, setValue]);

    const canSetAffiliate = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetAffiliate);
    const canSetAdvertiser = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetAdvertiser);
    const canSetIntegration = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetIntegration);
    const canSetChannel = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetChannel);

    return (
        <div className={"bg-gray-100 rounded-lg border-dashed p-1 "}>
            <div className="p-4 bg-white rounded-lg m-3">
                <Typography.Title level={4}>{tC("settings")}</Typography.Title>
                <Row gutter={16}>
                    <Col span={8}>
                        <FormItem control={control} name={fieldNames.name.name} label={tFields("name.label")} required>
                            <Input placeholder={tFields("name.placeholder")} />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.trigger?.name || "trigger"}
                            label={tFields("trigger.label")}
                        >
                            <TriggerSelectSearchable />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.is_active?.name || "is_active"}
                            label={tFields("state.label")}
                        >
                            <StatusSelectSearchable />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.form_structure_ids?.name || "form_structure_ids"}
                            label={tFields("form_structure.label")}
                        >
                            <FormStructureSelectSearchable
                                mode="multiple"
                                allowClear
                                placeholder={tFields("form_structure.placeholder")}
                            />
                        </FormItem>
                    </Col>
                </Row>
                {canSetOwner && (
                    <Row gutter={16}>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.owner_id?.name || "owner_id"}
                                label={tFields("owner.label")}
                            >
                                <UserSelectSearchable allowClear={false} />
                            </FormItem>
                        </Col>
                    </Row>
                )}
            </div>
            <div className="p-4 bg-white rounded-lg m-3">
                <Typography.Title level={4}>{t("sections.conditions")}</Typography.Title>
                <Row gutter={16}>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.sources?.name || "sources"}
                            label={tFields("source.label")}
                        >
                            <SourceSelectSearchable mode="multiple" allowClear />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.types?.name || "types"}
                            label={tFields("conversion_type.label")}
                        >
                            <ConversionTypeSelectSearchable mode="multiple" allowClear />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.affiliate_ids?.name || "affiliate_ids"}
                            label={tFields("affiliates.label")}
                        >
                            {canSetAffiliate ? (
                                <PartnerSelectSearchable mode="multiple" withAllOption />
                            ) : (
                                <Select
                                    disabled
                                    mode="multiple"
                                    options={
                                        isAffiliatePartner && currentUser
                                            ? [
                                                  {
                                                      label: currentUser.nickname ?? currentUser.email,
                                                      value: currentUser.id,
                                                  },
                                              ]
                                            : []
                                    }
                                />
                            )}
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.advertiser_ids?.name || "advertiser_ids"}
                            label={tFields("advertisers.label")}
                        >
                            {canSetAdvertiser ? (
                                <AdvertiserSelectSearchable mode="multiple" withAllOption />
                            ) : (
                                <Select
                                    disabled
                                    mode="multiple"
                                    options={[{ label: tC("all"), value: MULTISELECT_ALL_VALUE }]}
                                />
                            )}
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.integration_ids?.name || "integration_ids"}
                            label={tFields("integrations.label")}
                        >
                            {canSetIntegration ? (
                                <IntegrationSelectSearchable mode="multiple" withAllOption />
                            ) : (
                                <Select
                                    disabled
                                    mode="multiple"
                                    options={[{ label: tC("all"), value: MULTISELECT_ALL_VALUE }]}
                                />
                            )}
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.cascade_ids?.name || "cascade_ids"}
                            label={tFields("channels.label")}
                        >
                            {canSetChannel ? (
                                <CascadeSelectSearchable mode="multiple" withAllOption />
                            ) : (
                                <Select
                                    disabled
                                    mode="multiple"
                                    options={[{ label: tC("all"), value: MULTISELECT_ALL_VALUE }]}
                                />
                            )}
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <FormItem
                            control={control}
                            name={fieldNames.offer_ids?.name || "offer_ids"}
                            label={tFields("included_offers.label")}
                        >
                            <OfferSelectSearchable mode="multiple" withAllOption disabled={excludedHasSpecific} />
                        </FormItem>
                    </Col>
                    <Col span={12}>
                        <FormItem
                            control={control}
                            name={fieldNames.excluded_offer_ids?.name || "excluded_offer_ids"}
                            label={tFields("excluded_offers.label")}
                        >
                            <OfferSelectSearchable mode="multiple" withAllOption disabled={includedHasSpecific} />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.payout_result?.name || "payout_result"}
                            label={tFields("payout_result.label")}
                        >
                            <PayoutResultSelectSearchable allowClear />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.payout_type?.name || "payout_type"}
                            label={tFields("payout_type.label")}
                        >
                            <PayoutTypeSelectSearchable allowClear />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.country_id?.name || "country_id"}
                            label={tFields("country.label")}
                        >
                            <CountrySelectSearchable allowClear />
                        </FormItem>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export const AddTrackingToolToForm: FC<AddTrackingToolToFormProps> = ({ onSubmit, formId }) => {
    const { t } = usePaldockTranslation("forms.add-tracking-tool-to");
    const { t: tCommon } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<AddTrackingToolToFormData>();
    const { handleErrorsFromErrorResponse } = generalFormUtils;
    const userUtils = useUser();
    const permissionsUtils = usePermission();
    const canSetAffiliate = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetAffiliate);
    const isAffiliatePartner =
        userUtils.getActiveTenant()?.userRoles?.some((role) => role.name === RoleEnum.AffiliatePartner) ?? false;
    const storeRequestMutation = useStoreOutgoingPostbackMutation();
    const currentUserId = userUtils.getAuthenticatedUser()?.id;

    // Set default values to include "Vše" for affiliate, advertiser, and offer fields.
    const defaultValues: AddTrackingToolToFormData = {
        name: "",
        is_active: false,
        first_sequence_step_id: null,
        trigger: OutgoingPostbackTrigger.ConversionPayoutAll,
        payout_result: null,
        payout_type: null,
        affiliate_ids: canSetAffiliate
            ? [MULTISELECT_ALL_VALUE]
            : isAffiliatePartner && currentUserId
              ? [currentUserId]
              : [],
        advertiser_ids: [MULTISELECT_ALL_VALUE],
        offer_ids: [MULTISELECT_ALL_VALUE],
        integration_ids: [MULTISELECT_ALL_VALUE],
        cascade_ids: [MULTISELECT_ALL_VALUE],
        owner_id: userUtils.getAuthenticatedUser()?.id,
    };

    const handleMultiSelectValue = <T extends string | number>(
        arr: (T | typeof MULTISELECT_ALL_VALUE)[] = []
    ): Record<string, T> => {
        if (arr.includes(MULTISELECT_ALL_VALUE)) return {};
        return Object.fromEntries(arr.map((val) => [val, val])) as Record<string, T>;
    };

    const onSubmitForm = (data: AddTrackingToolToFormData) => {
        const payload = {
            name: data.name,
            is_active: data.is_active,
            first_sequence_step_id: data.first_sequence_step_id,
            types: handleMultiSelectValue(data.types),
            sources: handleMultiSelectValue(data.sources),
            offer_ids: handleMultiSelectValue(data.offer_ids),
            excluded_offer_ids: handleMultiSelectValue(data.excluded_offer_ids),
            country_id: data.country_id ?? null,
            affiliate_ids: handleMultiSelectValue(data.affiliate_ids),
            advertiser_ids: handleMultiSelectValue(data.advertiser_ids),
            integration_ids: handleMultiSelectValue(data.integration_ids),
            cascade_ids: handleMultiSelectValue(data.cascade_ids),
            trigger: data.trigger,
            payout_result: data.payout_result ?? null,
            payout_type: data.payout_type ?? null,
            owner_id: data.owner_id,
            form_structure_ids: data.form_structure_ids ?? [],
        } as StoreOutgoingPostbackRequest;

        storeRequestMutation.mutate(payload, {
            onSuccess: (response) => {
                showCustomToast(
                    {
                        title: t("messages.add_success"),
                        buttons: [
                            {
                                label: tCommon("open"),
                                href: response.outgoingPostback.id
                                    ? `/${userUtils.getActiveTenant()?.slug_name}/tool/tracking/tracking-api/${response.outgoingPostback.id}/general`
                                    : undefined,
                            },
                        ],
                    },
                    TOAST_TYPES.SUCCESS
                );
                onSubmit?.();
            },
            onError: (error) => {
                toast.error(`${t("messages.add_error")}: ${error.message}`);
                handleErrorsFromErrorResponse(error);
            },
        });
    };

    return (
        <div>
            <GeneralFormProvider<AddTrackingToolToFormData>
                id={formId}
                schema={schema}
                defaultValues={defaultValues}
                onSubmit={onSubmitForm}
            >
                <AddTrackingToolToFormContent />
            </GeneralFormProvider>
        </div>
    );
};
